/**
 * Global mount point for the Income/Expense (Кирим/Чиқим) modal.
 *
 * Always mounted at the app root, but `<TxnFormModal>` itself uses
 * `destroyOnHidden` so its form subtree only exists while open — no memory
 * cost when idle.
 *
 * Wiring:
 *   - Subscribes to `useTransactionModal` (shared store).
 *   - Casts the opaque `payload.mode` back to `TxnFormMode` at the boundary
 *     (this widget IS allowed to import from `features/accounting/`; the
 *     `shared/` store side is what cannot).
 *   - Intercepts `onSaved` to fire the per-open invalidation contract:
 *       1. `queryClient.invalidateQueries` for every prefix in
 *          `invalidateOnSuccess` (caller-specified — for derived queries the
 *          calling page owns).
 *       2. Caller's `onSuccess()` callback (for non-query side effects:
 *          navigation, analytics, etc.).
 *     The CANONICAL accounting invalidation (`/v1/accounting` prefix) is
 *     already handled inside the mutation hooks — this layer adds the
 *     cross-feature derived-data refresh.
 *
 * One TanStack `invalidateQueries` call per prefix is intentional: the
 * default behaviour matches by key-array prefix (no `exact: true`), so a
 * caller passing `['/v1/finance']` invalidates the whole finance subtree.
 * Matches the project's `useEntityInvalidate` semantics.
 */

import { TxnFormModal, type TxnFormMode } from '@/features/accounting/ui/components/ledger'
import { useTransactionModal } from '@/shared/stores'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

export function TransactionModalRoot() {
  const queryClient = useQueryClient()
  const { isOpen, payload, close } = useTransactionModal()

  const handleSaved = useCallback(() => {
    if (payload?.invalidateOnSuccess) {
      for (const prefix of payload.invalidateOnSuccess) {
        const queryKey = Array.isArray(prefix) ? prefix : [prefix]
        queryClient.invalidateQueries({ queryKey })
      }
    }
    payload?.onSuccess?.()
  }, [payload, queryClient])

  // payload.mode is structurally a TxnFormMode (see store contract); the
  // store keeps it opaque to respect FSD shared→features import direction.
  const mode = (payload?.mode ?? null) as TxnFormMode | null

  return <TxnFormModal open={isOpen} mode={mode} onClose={close} onSaved={handleSaved} />
}
